/**
 * MindDeck Local Storage Service
 * Uses IndexedDB for persistent local storage of all data
 */

const DB_NAME = 'minddeck';
const DB_VERSION = 1;

// Store names
const STORES = {
  WORKSPACES: 'workspaces',
  CARDS: 'cards',
  DECKS: 'decks',
  DELETED_ITEMS: 'deleted_items',
  SETTINGS: 'settings'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export async function initDB() {
  if (db) return db;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Workspaces store
      if (!database.objectStoreNames.contains(STORES.WORKSPACES)) {
        const wsStore = database.createObjectStore(STORES.WORKSPACES, { keyPath: 'id' });
        wsStore.createIndex('is_main', 'is_main', { unique: false });
        wsStore.createIndex('is_deleted', 'is_deleted', { unique: false });
      }

      // Cards store
      if (!database.objectStoreNames.contains(STORES.CARDS)) {
        const cardStore = database.createObjectStore(STORES.CARDS, { keyPath: 'id' });
        cardStore.createIndex('workspace_id', 'workspace_id', { unique: false });
        cardStore.createIndex('is_deleted', 'is_deleted', { unique: false });
        cardStore.createIndex('workspace_deleted', ['workspace_id', 'is_deleted'], { unique: false });
      }

      // Decks store
      if (!database.objectStoreNames.contains(STORES.DECKS)) {
        const deckStore = database.createObjectStore(STORES.DECKS, { keyPath: 'id' });
        deckStore.createIndex('workspace_id', 'workspace_id', { unique: false });
        deckStore.createIndex('is_deleted', 'is_deleted', { unique: false });
        deckStore.createIndex('workspace_deleted', ['workspace_id', 'is_deleted'], { unique: false });
      }

      // Deleted items store (for undo)
      if (!database.objectStoreNames.contains(STORES.DELETED_ITEMS)) {
        const deletedStore = database.createObjectStore(STORES.DELETED_ITEMS, { keyPath: 'id' });
        deletedStore.createIndex('deleted_at', 'deleted_at', { unique: false });
      }

      // Settings store
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Generate a UUID
 */
export function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Get current ISO timestamp
 */
function now() {
  return new Date().toISOString();
}

// ============== WORKSPACE OPERATIONS ==============

export async function getWorkspaces() {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.WORKSPACES, 'readonly');
    const store = tx.objectStore(STORES.WORKSPACES);
    const request = store.getAll();
    
    request.onsuccess = () => {
      const workspaces = request.result.filter(w => !w.is_deleted);
      resolve(workspaces);
    };
    request.onerror = () => reject(request.error);
  });
}

// Track if main workspace initialization is in progress (singleton promise pattern)
let mainWorkspacePromise = null;

export async function getMainWorkspace() {
  await initDB();
  
  // If initialization is already in progress, wait for it
  if (mainWorkspacePromise) {
    return mainWorkspacePromise;
  }
  
  // Set the promise FIRST to prevent race conditions
  mainWorkspacePromise = (async () => {
    try {
      const workspaces = await getWorkspaces();
      let main = workspaces.find(w => w.is_main);
      
      if (!main) {
        // Double-check inside the lock
        const recheckWorkspaces = await getWorkspaces();
        const existingMain = recheckWorkspaces.find(w => w.is_main);
        
        if (existingMain) {
          return existingMain;
        }
        
        // Create new Main workspace
        main = {
          id: generateId(),
          name: 'Main',
          description: 'Your main workspace',
          is_main: true,
          is_deleted: false,
          created_at: now(),
          updated_at: now()
        };
        await saveWorkspace(main);
      }
      
      return main;
    } finally {
      // Clear the promise after completion so future calls can run fresh
      // This allows proper re-initialization if DB is cleared
      mainWorkspacePromise = null;
    }
  })();
  
  return mainWorkspacePromise;
}

export async function getWorkspace(id) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.WORKSPACES, 'readonly');
    const store = tx.objectStore(STORES.WORKSPACES);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveWorkspace(workspace) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.WORKSPACES, 'readwrite');
    const store = tx.objectStore(STORES.WORKSPACES);
    
    workspace.updated_at = now();
    const request = store.put(workspace);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(workspace);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

export async function createWorkspace(name, description = '') {
  const workspace = {
    id: generateId(),
    name,
    description,
    is_main: false,
    is_deleted: false,
    created_at: now(),
    updated_at: now()
  };
  return saveWorkspace(workspace);
}

export async function updateWorkspace(id, updates) {
  const workspace = await getWorkspace(id);
  if (!workspace) throw new Error('Workspace not found');
  
  const updatedWorkspace = {
    ...workspace,
    ...updates,
    updated_at: now()
  };
  
  return saveWorkspace(updatedWorkspace);
}

export async function deleteWorkspace(id) {
  const workspace = await getWorkspace(id);
  if (!workspace) throw new Error('Workspace not found');
  
  // Get all cards and decks for undo
  const cards = await getCards(id);
  const decks = await getDecks(id);
  
  // Store for undo
  await saveDeletedItem({
    id: generateId(),
    item_type: 'workspace',
    item_data: workspace,
    cascade_data: [{ cards, decks }],
    deleted_at: now()
  });
  
  // Soft delete workspace and contents
  workspace.is_deleted = true;
  workspace.deleted_at = now();
  await saveWorkspace(workspace);
  
  for (const card of cards) {
    card.is_deleted = true;
    card.deleted_at = now();
    await saveCard(card);
  }
  
  for (const deck of decks) {
    deck.is_deleted = true;
    deck.deleted_at = now();
    await saveDeck(deck);
  }
  
  return { message: 'Workspace deleted', id };
}

export async function duplicateWorkspace(id) {
  const workspace = await getWorkspace(id);
  if (!workspace) throw new Error('Workspace not found');
  
  // Create ID mapping
  const idMap = {};
  
  // Create new workspace
  const newWorkspace = {
    ...workspace,
    id: generateId(),
    name: `${workspace.name} (Copy)`,
    is_main: false,
    created_at: now(),
    updated_at: now()
  };
  await saveWorkspace(newWorkspace);
  
  // Clone cards
  const cards = await getCards(id);
  for (const card of cards) {
    const oldId = card.id;
    const newId = generateId();
    idMap[oldId] = newId;
    
    const newCard = {
      ...card,
      id: newId,
      workspace_id: newWorkspace.id,
      decks: [],
      created_at: now(),
      updated_at: now()
    };
    await saveCard(newCard);
  }
  
  // Update card_id references in fields
  const newCards = await getCards(newWorkspace.id);
  for (const card of newCards) {
    let updated = false;
    const newFields = { ...card.fields };
    
    for (const [fn, fv] of Object.entries(newFields)) {
      if (fv && typeof fv === 'object' && fv.card_id && idMap[fv.card_id]) {
        newFields[fn] = { ...fv, card_id: idMap[fv.card_id] };
        updated = true;
      }
    }
    
    if (updated) {
      card.fields = newFields;
      await saveCard(card);
    }
  }
  
  // Clone decks
  const decks = await getDecks(id);
  const deckIdMap = {};
  
  for (const deck of decks) {
    const newDeckId = generateId();
    deckIdMap[deck.id] = newDeckId;
    
    const newDeck = {
      ...deck,
      id: newDeckId,
      workspace_id: newWorkspace.id,
      cards: deck.cards.map(c => ({
        ...c,
        card_id: idMap[c.card_id] || c.card_id
      })),
      created_at: now(),
      updated_at: now()
    };
    await saveDeck(newDeck);
  }
  
  // Update card deck references
  for (const card of newCards) {
    if (card.decks?.length > 0) {
      card.decks = card.decks.map(d => deckIdMap[d] || d).filter(d => deckIdMap[d] !== undefined || !decks.find(od => od.id === d));
      await saveCard(card);
    }
  }
  
  return {
    message: 'Workspace duplicated',
    original_id: id,
    new_id: newWorkspace.id,
    cards_copied: cards.length,
    decks_copied: decks.length
  };
}

// ============== CARD OPERATIONS ==============

export async function getCards(workspaceId, filters = {}) {
  await initDB();
  
  // Guard against null/undefined workspaceId - return empty array
  if (!workspaceId || typeof workspaceId !== 'string') {
    console.warn('getCards called without valid workspaceId');
    return [];
  }
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CARDS, 'readonly');
    const store = tx.objectStore(STORES.CARDS);
    
    // Use workspace_id index instead of compound index to avoid boolean key issues
    const index = store.index('workspace_id');
    const request = index.getAll(workspaceId);
    
    request.onsuccess = async () => {
      // Filter out deleted cards since we're using workspace_id index only
      let cards = (request.result || []).filter(c => !c.is_deleted);
      
      // Filter out empty cards (no fields AND no body) - auto cleanup
      const emptyCardIds = [];
      cards = cards.filter(c => {
        const hasFields = c.fields && Object.keys(c.fields).length > 0;
        const hasBody = c.body && c.body.trim().length > 0;
        if (!hasFields && !hasBody) {
          emptyCardIds.push(c.id);
          return false; // Exclude from results
        }
        return true;
      });
      
      // Auto-delete empty cards in background (don't await to avoid blocking UI)
      if (emptyCardIds.length > 0) {
        softDeleteEmptyCards(workspaceId, emptyCardIds);
      }
      
      // Apply filters
      if (filters.deck) {
        cards = cards.filter(c => c.decks?.includes(filters.deck));
      }
      if (filters.tag) {
        cards = cards.filter(c => c.tags?.includes(filters.tag));
      }
      if (filters.search) {
        const q = filters.search.toLowerCase();
        cards = cards.filter(c => 
          c.title.toLowerCase().includes(q) ||
          c.body?.toLowerCase().includes(q)
        );
      }
      
      resolve(cards);
    };
    request.onerror = () => reject(request.error);
  });
}

// Helper to soft-delete empty cards without blocking
async function softDeleteEmptyCards(workspaceId, cardIds) {
  try {
    await initDB();
    const timestamp = now();
    const tx = db.transaction(STORES.CARDS, 'readwrite');
    const store = tx.objectStore(STORES.CARDS);
    
    for (const cardId of cardIds) {
      const card = await new Promise((resolve, reject) => {
        const req = store.get(cardId);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
      
      if (card && card.workspace_id === workspaceId && !card.is_deleted) {
        card.is_deleted = true;
        card.deleted_at = timestamp;
        store.put(card);
      }
    }
  } catch (error) {
    console.warn('Failed to auto-delete empty cards:', error);
  }
}

export async function getCard(workspaceId, cardId) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CARDS, 'readonly');
    const store = tx.objectStore(STORES.CARDS);
    const request = store.get(cardId);
    
    request.onsuccess = () => {
      const card = request.result;
      if (card && card.workspace_id === workspaceId && !card.is_deleted) {
        // Check if card is empty (no fields AND no body) - treat as not found
        const hasFields = card.fields && Object.keys(card.fields).length > 0;
        const hasBody = card.body && card.body.trim().length > 0;
        
        if (!hasFields && !hasBody) {
          // Auto-delete this empty card in background
          softDeleteEmptyCards(workspaceId, [cardId]);
          resolve(null);
        } else {
          resolve(card);
        }
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

export async function saveCard(card) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.CARDS, 'readwrite');
    const store = tx.objectStore(STORES.CARDS);
    
    card.updated_at = now();
    const request = store.put(card);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(card);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

/**
 * Check if a card has content (notes/body OR at least one field)
 */
export function cardHasContent(data) {
  const hasBody = data.body && data.body.trim().length > 0;
  const hasFields = data.fields && Object.keys(data.fields).length > 0;
  return hasBody || hasFields;
}

export async function createCard(workspaceId, data) {
  // Guard against null workspaceId
  if (!workspaceId) {
    throw new Error('Workspace ID is required to create a card');
  }
  
  // Validate that card has content (body/notes OR at least one field)
  const hasBody = data.body && data.body.trim().length > 0;
  const hasFields = data.fields && Object.keys(data.fields).length > 0;
  
  if (!hasBody && !hasFields) {
    throw new Error('Card must have at least one field or a note to be saved');
  }
  
  const timestamp = now();
  
  // Process fields
  const fields = {};
  if (data.fields) {
    for (const [key, value] of Object.entries(data.fields)) {
      if (typeof value === 'object' && 'value' in value) {
        fields[key] = { ...value, created_at: value.created_at || timestamp };
      } else {
        fields[key] = { value, card_id: null, created_at: timestamp };
      }
    }
  }
  
  const card = {
    id: generateId(),
    workspace_id: workspaceId,
    title: data.title,
    body: data.body || '',
    fields,
    tags: data.tags || [],
    decks: data.decks || [],
    is_deleted: false,
    created_at: timestamp,
    updated_at: timestamp
  };
  
  await saveCard(card);
  
  // Create bidirectional links
  await createBidirectionalCards(workspaceId, card.title, card.id, fields);
  
  return card;
}

export async function updateCard(workspaceId, cardId, updates) {
  const card = await getCard(workspaceId, cardId);
  if (!card) throw new Error('Card not found');
  
  const timestamp = now();
  
  // Process field updates - REPLACE fields entirely (to support deletions)
  if (updates.fields !== undefined) {
    const newFields = {};
    
    for (const [key, value] of Object.entries(updates.fields || {})) {
      const oldField = card.fields?.[key];
      const oldCreated = oldField?.created_at;
      
      if (typeof value === 'object' && 'value' in value) {
        newFields[key] = { ...value, created_at: value.created_at || oldCreated || timestamp };
      } else {
        newFields[key] = { value, card_id: null, created_at: oldCreated || timestamp };
      }
    }
    
    updates.fields = newFields;
    
    // Create bidirectional links for new fields
    await createBidirectionalCards(workspaceId, card.title, cardId, newFields);
  }
  
  // Check if updated card would be empty (no body AND no fields)
  const finalBody = updates.body !== undefined ? updates.body : card.body;
  const finalFields = updates.fields !== undefined ? updates.fields : card.fields;
  const hasBody = finalBody && finalBody.trim().length > 0;
  const hasFields = finalFields && Object.keys(finalFields).length > 0;
  
  if (!hasBody && !hasFields) {
    throw new Error('Card must have at least one field or a note to be saved');
  }
  
  const updatedCard = { ...card, ...updates, updated_at: timestamp };
  await saveCard(updatedCard);
  
  return updatedCard;
}

export async function deleteCard(workspaceId, cardId, cascade = true) {
  const card = await getCard(workspaceId, cardId);
  if (!card) throw new Error('Card not found');
  
  const timestamp = now();
  const cardTitle = card.title;
  const cascadeData = [];
  let fieldsRemoved = 0;
  
  // Cascade delete - remove references from other cards
  if (cascade && cardTitle) {
    const allCards = await getCards(workspaceId);
    
    for (const otherCard of allCards) {
      if (otherCard.id === cardId) continue;
      
      const fieldsToRemove = [];
      
      for (const [fieldName, fieldData] of Object.entries(otherCard.fields || {})) {
        let shouldRemove = false;
        
        if (fieldData?.card_id === cardId) {
          shouldRemove = true;
        } else if (fieldName.toLowerCase() === cardTitle.toLowerCase()) {
          shouldRemove = true;
        } else {
          const fieldValue = fieldData?.value || fieldData;
          if (fieldValue && String(fieldValue).toLowerCase() === cardTitle.toLowerCase()) {
            shouldRemove = true;
          }
        }
        
        if (shouldRemove) {
          fieldsToRemove.push(fieldName);
        }
      }
      
      if (fieldsToRemove.length > 0) {
        const removedFields = {};
        for (const fn of fieldsToRemove) {
          removedFields[fn] = otherCard.fields[fn];
          delete otherCard.fields[fn];
        }
        
        cascadeData.push({
          card_id: otherCard.id,
          removed_fields: removedFields
        });
        
        otherCard.updated_at = timestamp;
        await saveCard(otherCard);
        fieldsRemoved += fieldsToRemove.length;
      }
    }
  }
  
  // Store for undo
  await saveDeletedItem({
    id: generateId(),
    item_type: 'card',
    workspace_id: workspaceId,
    item_data: card,
    cascade_data: cascadeData,
    deleted_at: timestamp
  });
  
  // Soft delete
  card.is_deleted = true;
  card.deleted_at = timestamp;
  await saveCard(card);
  
  // Clean up orphaned index cards (cards with no fields left)
  await cleanupOrphanedCards(workspaceId);
  
  return { message: 'Card deleted', id: cardId, fields_removed: fieldsRemoved };
}

// Clean up cards that have become empty (no fields AND no body)
export async function cleanupOrphanedCards(workspaceId) {
  const allCards = await getCards(workspaceId);
  let cleaned = 0;
  
  for (const card of allCards) {
    // Check if card has any fields left
    const fieldCount = Object.keys(card.fields || {}).length;
    const hasBody = card.body && card.body.trim().length > 0;
    
    // Delete any card that has no fields AND no body content
    if (fieldCount === 0 && !hasBody) {
      card.is_deleted = true;
      card.deleted_at = now();
      await saveCard(card);
      cleaned++;
    }
  }
  
  return { cleaned };
}

export async function getCardDeletionPreview(workspaceId, cardId) {
  const card = await getCard(workspaceId, cardId);
  if (!card) throw new Error('Card not found');
  
  const cardTitle = card.title;
  const allCards = await getCards(workspaceId);
  const affectedCards = [];
  let totalFieldsRemoved = 0;
  
  for (const otherCard of allCards) {
    if (otherCard.id === cardId) continue;
    
    const fieldsToRemove = [];
    
    for (const [fieldName, fieldData] of Object.entries(otherCard.fields || {})) {
      let shouldRemove = false;
      let reason = '';
      
      if (fieldData?.card_id === cardId) {
        shouldRemove = true;
        reason = 'linked to this card';
      } else if (fieldName.toLowerCase() === cardTitle.toLowerCase()) {
        shouldRemove = true;
        reason = 'field name matches card';
      } else {
        const fieldValue = fieldData?.value || fieldData;
        if (fieldValue && String(fieldValue).toLowerCase() === cardTitle.toLowerCase()) {
          shouldRemove = true;
          reason = 'field value matches card';
        }
      }
      
      if (shouldRemove) {
        fieldsToRemove.push({
          field_name: fieldName,
          field_value: fieldData?.value || fieldData,
          reason
        });
      }
    }
    
    if (fieldsToRemove.length > 0) {
      affectedCards.push({
        card_id: otherCard.id,
        card_title: otherCard.title,
        fields_to_remove: fieldsToRemove
      });
      totalFieldsRemoved += fieldsToRemove.length;
    }
  }
  
  return {
    card_id: cardId,
    card_title: cardTitle,
    affected_cards: affectedCards,
    total_fields_removed: totalFieldsRemoved
  };
}

export async function getCardReferences(workspaceId, cardId) {
  const allCards = await getCards(workspaceId);
  const referencing = [];
  
  for (const card of allCards) {
    if (card.id === cardId) continue;
    
    for (const [, fieldData] of Object.entries(card.fields || {})) {
      if (fieldData?.card_id === cardId) {
        referencing.push(card);
        break;
      }
    }
  }
  
  return referencing;
}

// ============== BIDIRECTIONAL LINKING ==============

async function createBidirectionalCards(workspaceId, sourceTitle, sourceId, fields) {
  if (!fields || Object.keys(fields).length === 0) return;
  
  const timestamp = now();
  
  for (const [fieldName, fieldData] of Object.entries(fields)) {
    const fieldValue = fieldData?.value || fieldData;
    if (!fieldValue) continue;
    
    // Parse components (handle comma-separated and dates)
    let components = [String(fieldValue)];
    
    const isSlashDate = /^\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}$/.test(fieldValue.trim());
    const writtenDateMatch = String(fieldValue).match(/^([A-Za-z]+\s+\d{1,2}(?:st|nd|rd|th)?),?\s*(\d{4})$/i);
    
    if (isSlashDate) {
      components = [fieldValue];
    } else if (writtenDateMatch) {
      components = [fieldValue.trim(), writtenDateMatch[1].trim(), writtenDateMatch[2]];
    } else if (fieldValue.includes(',')) {
      const parts = fieldValue.split(',').map(p => p.trim()).filter(Boolean);
      if (parts.length > 1) {
        components = [fieldValue.trim(), ...parts];
      }
    }
    
    for (const component of components) {
      if (!component) continue;
      
      // Find or create card for value
      const existingValueCards = await searchCards(workspaceId, component);
      let valueCard = existingValueCards.find(c => c.title.toLowerCase() === component.toLowerCase());
      
      if (valueCard) {
        // Append sourceTitle to comma-separated list rather than overwriting.
        // e.g. Fire.element becomes "Aries, Leo, Sagittarius" not just "Sagittarius"
        if (!valueCard.fields) valueCard.fields = {};
        const existingVField = valueCard.fields[fieldName];
        const existingVValue = existingVField?.value || '';
        const existingVValues = existingVValue
          ? existingVValue.split(',').map(v => v.trim()).filter(Boolean)
          : [];
        if (!existingVValues.includes(sourceTitle)) {
          const newVValue = existingVValues.length > 0
            ? existingVValue + ', ' + sourceTitle
            : sourceTitle;
          valueCard.fields[fieldName] = {
            value: newVValue,
            card_id: existingVField?.card_id || sourceId,
            created_at: existingVField?.created_at || timestamp
          };
          await saveCard(valueCard);
        }
      } else {
        // Create new card
        valueCard = {
          id: generateId(),
          workspace_id: workspaceId,
          title: component,
          body: `Index card from '${sourceTitle}'`,
          fields: {
            [fieldName]: { value: sourceTitle, card_id: sourceId, created_at: timestamp }
          },
          tags: [],
          decks: [],
          is_deleted: false,
          created_at: timestamp,
          updated_at: timestamp
        };
        await saveCard(valueCard);
      }
      
      // Find or create card for field name
      const existingFieldCards = await searchCards(workspaceId, fieldName);
      let fieldCard = existingFieldCards.find(c => c.title.toLowerCase() === fieldName.toLowerCase());
      
      if (fieldCard) {
        // Same append logic — element index card gets "Aries, Leo, Sagittarius" under Fire
        if (!fieldCard.fields) fieldCard.fields = {};
        const existingFField = fieldCard.fields[component];
        const existingFValue = existingFField?.value || '';
        const existingFValues = existingFValue
          ? existingFValue.split(',').map(v => v.trim()).filter(Boolean)
          : [];
        if (!existingFValues.includes(sourceTitle)) {
          const newFValue = existingFValues.length > 0
            ? existingFValue + ', ' + sourceTitle
            : sourceTitle;
          fieldCard.fields[component] = {
            value: newFValue,
            card_id: existingFField?.card_id || sourceId,
            created_at: existingFField?.created_at || timestamp
          };
          await saveCard(fieldCard);
        }
      } else {
        fieldCard = {
          id: generateId(),
          workspace_id: workspaceId,
          title: fieldName,
          body: `Index card for '${fieldName}' attribute`,
          fields: {
            [component]: { value: sourceTitle, card_id: sourceId, created_at: timestamp }
          },
          tags: [],
          decks: [],
          is_deleted: false,
          created_at: timestamp,
          updated_at: timestamp
        };
        await saveCard(fieldCard);
      }
    }
  }
}

export async function searchCards(workspaceId, query) {
  const cards = await getCards(workspaceId);
  const q = query.toLowerCase();
  return cards.filter(c => 
    c.title.toLowerCase().includes(q) ||
    c.body?.toLowerCase().includes(q)
  );
}

// ============== DECK OPERATIONS ==============

export async function getDecks(workspaceId) {
  await initDB();
  
  // Guard against null/undefined workspaceId - return empty array
  if (!workspaceId || typeof workspaceId !== 'string') {
    console.warn('getDecks called without valid workspaceId');
    return [];
  }
  
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DECKS, 'readonly');
    const store = tx.objectStore(STORES.DECKS);
    // Use workspace_id index instead of compound index to avoid boolean key issues
    const index = store.index('workspace_id');
    const request = index.getAll(workspaceId);
    
    request.onsuccess = () => {
      // Filter out deleted decks
      const decks = (request.result || []).filter(d => !d.is_deleted);
      resolve(decks);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function getDeck(workspaceId, deckId) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DECKS, 'readonly');
    const store = tx.objectStore(STORES.DECKS);
    const request = store.get(deckId);
    
    request.onsuccess = () => {
      const deck = request.result;
      if (deck && deck.workspace_id === workspaceId && !deck.is_deleted) {
        resolve(deck);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

export async function saveDeck(deck) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DECKS, 'readwrite');
    const store = tx.objectStore(STORES.DECKS);
    
    deck.updated_at = now();
    const request = store.put(deck);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(deck);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

export async function computeDeckClosure(workspaceId, seedCardIds) {
  if (!seedCardIds || seedCardIds.length === 0) return [];
  
  const includedIds = new Set(seedCardIds);
  const toProcess = [...seedCardIds];
  const allCards = await getCards(workspaceId);
  const cardsById = Object.fromEntries(allCards.map(c => [c.id, c]));
  
  while (toProcess.length > 0) {
    const currentId = toProcess.shift();
    const card = cardsById[currentId];
    if (!card) continue;
    
    // Find connected cards through fields
    for (const [, fieldData] of Object.entries(card.fields || {})) {
      if (fieldData?.card_id && !includedIds.has(fieldData.card_id)) {
        includedIds.add(fieldData.card_id);
        toProcess.push(fieldData.card_id);
      }
    }
    
    // Find cards that reference this card
    for (const otherCard of allCards) {
      if (includedIds.has(otherCard.id)) continue;
      
      for (const [, fieldData] of Object.entries(otherCard.fields || {})) {
        if (fieldData?.card_id === currentId) {
          includedIds.add(otherCard.id);
          toProcess.push(otherCard.id);
          break;
        }
      }
    }
  }
  
  return Array.from(includedIds);
}

export async function createDeck(workspaceId, name, description, seedCardIds) {
  // Compute closure
  const closedCardIds = await computeDeckClosure(workspaceId, seedCardIds);
  
  const deck = {
    id: generateId(),
    workspace_id: workspaceId,
    name,
    description: description || '',
    cards: closedCardIds.map((cardId, i) => ({ card_id: cardId, position: i, note: null })),
    is_deleted: false,
    created_at: now(),
    updated_at: now()
  };
  
  await saveDeck(deck);
  
  // Update cards to include this deck
  for (const cardId of closedCardIds) {
    const card = await getCard(workspaceId, cardId);
    if (card) {
      if (!card.decks) card.decks = [];
      if (!card.decks.includes(deck.id)) {
        card.decks.push(deck.id);
        await saveCard(card);
      }
    }
  }
  
  return {
    deck,
    seed_cards: seedCardIds.length,
    total_cards_after_closure: closedCardIds.length,
    auto_included: closedCardIds.length - seedCardIds.length
  };
}

export async function addCardsToDeck(workspaceId, deckId, seedCardIds) {
  const deck = await getDeck(workspaceId, deckId);
  if (!deck) throw new Error('Deck not found');
  
  const existingIds = new Set(deck.cards.map(c => c.card_id));
  const newSeedIds = seedCardIds.filter(id => !existingIds.has(id));
  
  if (newSeedIds.length === 0) {
    return { message: 'All cards already in deck', added: 0 };
  }
  
  // Compute closure for new cards
  const closedIds = await computeDeckClosure(workspaceId, newSeedIds);
  const idsToAdd = closedIds.filter(id => !existingIds.has(id));
  
  // Add to deck
  const startPosition = deck.cards.length;
  for (let i = 0; i < idsToAdd.length; i++) {
    deck.cards.push({ card_id: idsToAdd[i], position: startPosition + i, note: null });
  }
  
  await saveDeck(deck);
  
  // Update cards
  for (const cardId of idsToAdd) {
    const card = await getCard(workspaceId, cardId);
    if (card) {
      if (!card.decks) card.decks = [];
      if (!card.decks.includes(deckId)) {
        card.decks.push(deckId);
        await saveCard(card);
      }
    }
  }
  
  return {
    message: 'Cards added to deck',
    seed_cards: seedCardIds.length,
    total_added: idsToAdd.length,
    auto_included: idsToAdd.length - newSeedIds.length
  };
}

export async function deleteDeck(workspaceId, deckId) {
  const deck = await getDeck(workspaceId, deckId);
  if (!deck) throw new Error('Deck not found');
  
  // Store for undo
  await saveDeletedItem({
    id: generateId(),
    item_type: 'deck',
    workspace_id: workspaceId,
    item_data: deck,
    cascade_data: [],
    deleted_at: now()
  });
  
  // Remove deck from cards
  for (const cardRef of deck.cards) {
    const card = await getCard(workspaceId, cardRef.card_id);
    if (card && card.decks) {
      card.decks = card.decks.filter(d => d !== deckId);
      await saveCard(card);
    }
  }
  
  // Soft delete
  deck.is_deleted = true;
  deck.deleted_at = now();
  await saveDeck(deck);
  
  return { message: 'Deck deleted', id: deckId };
}

export async function exportDeck(workspaceId, deckId) {
  const deck = await getDeck(workspaceId, deckId);
  if (!deck) throw new Error('Deck not found');
  
  const cardIds = deck.cards.map(c => c.card_id);
  const cards = [];
  
  for (const cardId of cardIds) {
    const card = await getCard(workspaceId, cardId);
    if (card) {
      // Clean card for export
      const exportCard = {
        title: card.title,
        body: card.body,
        fields: {},
        tags: card.tags
      };
      
      // Simplify fields
      for (const [fn, fv] of Object.entries(card.fields || {})) {
        exportCard.fields[fn] = fv?.value || fv;
      }
      
      cards.push(exportCard);
    }
  }
  
  return {
    deck_name: deck.name,
    deck_description: deck.description,
    exported_at: now(),
    cards
  };
}

// ============== IMPORT ==============

export async function previewImport(workspaceId, deckName, cards) {
  const existingCards = await getCards(workspaceId);
  const previewCards = [];
  let mergeCount = 0;
  let createCount = 0;
  
  for (const incomingCard of cards) {
    const title = incomingCard.title?.trim();
    if (!title) continue;
    
    const existing = existingCards.find(c => c.title.toLowerCase() === title.toLowerCase());
    const incomingFields = incomingCard.fields || {};
    const incomingFieldCount = Object.keys(incomingFields).length;
    
    if (existing) {
      const existingFields = existing.fields || {};
      const newFields = Object.keys(incomingFields).filter(fn => !(fn in existingFields));
      
      previewCards.push({
        title,
        action: 'merge',
        existing_card_id: existing.id,
        existing_field_count: Object.keys(existingFields).length,
        incoming_field_count: incomingFieldCount,
        resulting_field_count: Object.keys(existingFields).length + newFields.length,
        new_fields: newFields
      });
      mergeCount++;
    } else {
      previewCards.push({
        title,
        action: 'create',
        incoming_field_count: incomingFieldCount,
        resulting_field_count: incomingFieldCount,
        new_fields: Object.keys(incomingFields)
      });
      createCount++;
    }
  }
  
  // Store preview for confirmation
  const previewId = generateId();
  await saveSetting(`import_preview_${previewId}`, {
    workspace_id: workspaceId,
    deck_name: deckName,
    cards: previewCards,
    import_data: { deck_name: deckName, cards }
  });
  
  return {
    preview_id: previewId,
    deck_name: deckName,
    total_cards: previewCards.length,
    cards_to_merge: mergeCount,
    cards_to_create: createCount,
    cards: previewCards
  };
}

export async function confirmImport(workspaceId, previewId) {
  const preview = await getSetting(`import_preview_${previewId}`);
  if (!preview || preview.workspace_id !== workspaceId) {
    throw new Error('Preview not found or expired');
  }
  
  const { deck_name, cards: importCards } = preview.import_data;
  const timestamp = now();
  const createdCards = [];
  const mergedCards = [];
  const deckCardIds = [];
  
  for (const incomingCard of importCards) {
    const title = incomingCard.title?.trim();
    if (!title) continue;
    
    // Process fields
    const incomingFields = {};
    for (const [fn, fv] of Object.entries(incomingCard.fields || {})) {
      if (typeof fv === 'object' && 'value' in fv) {
        incomingFields[fn] = { ...fv, created_at: fv.created_at || timestamp };
      } else {
        incomingFields[fn] = { value: fv, card_id: null, created_at: timestamp };
      }
    }
    
    const existingCards = await searchCards(workspaceId, title);
    const existing = existingCards.find(c => c.title.toLowerCase() === title.toLowerCase());
    
    if (existing) {
      // Merge fields
      const mergedFields = { ...existing.fields };
      for (const [fn, fv] of Object.entries(incomingFields)) {
        if (!(fn in mergedFields)) {
          mergedFields[fn] = fv;
        }
      }
      
      existing.fields = mergedFields;
      existing.updated_at = timestamp;
      await saveCard(existing);
      mergedCards.push(existing.id);
      deckCardIds.push(existing.id);
    } else {
      // Create new card
      const newCard = await createCard(workspaceId, {
        title,
        body: incomingCard.body || '',
        fields: incomingFields,
        tags: incomingCard.tags || []
      });
      createdCards.push(newCard.id);
      deckCardIds.push(newCard.id);
    }
  }
  
  // Create deck
  const deck = {
    id: generateId(),
    workspace_id: workspaceId,
    name: deck_name,
    description: `Imported on ${new Date().toLocaleString()}`,
    cards: deckCardIds.map((cardId, i) => ({ card_id: cardId, position: i, note: null })),
    is_deleted: false,
    created_at: timestamp,
    updated_at: timestamp
  };
  await saveDeck(deck);
  
  // Update cards with deck
  for (const cardId of deckCardIds) {
    const card = await getCard(workspaceId, cardId);
    if (card) {
      if (!card.decks) card.decks = [];
      if (!card.decks.includes(deck.id)) {
        card.decks.push(deck.id);
        await saveCard(card);
      }
    }
  }
  
  // Clean up preview
  await deleteSetting(`import_preview_${previewId}`);
  
  return {
    message: 'Import complete',
    deck_id: deck.id,
    deck_name: deck.name,
    cards_created: createdCards.length,
    cards_merged: mergedCards.length,
    total_in_deck: deckCardIds.length
  };
}

// ============== UNDO ==============

export async function saveDeletedItem(item) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DELETED_ITEMS, 'readwrite');
    const store = tx.objectStore(STORES.DELETED_ITEMS);
    const request = store.put(item);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve(item);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

export async function getLastDeletedItem() {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DELETED_ITEMS, 'readonly');
    const store = tx.objectStore(STORES.DELETED_ITEMS);
    const index = store.index('deleted_at');
    const request = index.openCursor(null, 'prev');
    
    request.onsuccess = () => {
      const cursor = request.result;
      if (cursor) {
        resolve(cursor.value);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

export async function undoLastDelete() {
  const deleted = await getLastDeletedItem();
  if (!deleted) throw new Error('Nothing to undo');
  
  const timestamp = now();
  
  if (deleted.item_type === 'card') {
    // Restore card
    const card = deleted.item_data;
    card.is_deleted = false;
    card.deleted_at = null;
    card.updated_at = timestamp;
    await saveCard(card);
    
    // Restore cascade-deleted fields
    for (const cascade of deleted.cascade_data || []) {
      const otherCard = await getCard(deleted.workspace_id, cascade.card_id);
      if (otherCard) {
        for (const [fn, fv] of Object.entries(cascade.removed_fields || {})) {
          otherCard.fields[fn] = fv;
        }
        otherCard.updated_at = timestamp;
        await saveCard(otherCard);
      }
    }
    
    // Remove from deleted items
    await removeDeletedItem(deleted.id);
    
    return {
      message: 'Card restored',
      card_id: card.id,
      card_title: card.title,
      fields_restored: deleted.cascade_data?.reduce((sum, c) => sum + Object.keys(c.removed_fields || {}).length, 0) || 0
    };
  }
  
  if (deleted.item_type === 'deck') {
    const deck = deleted.item_data;
    deck.is_deleted = false;
    deck.deleted_at = null;
    deck.updated_at = timestamp;
    await saveDeck(deck);
    
    // Re-add deck to cards
    for (const cardRef of deck.cards || []) {
      const card = await getCard(deleted.workspace_id, cardRef.card_id);
      if (card) {
        if (!card.decks) card.decks = [];
        if (!card.decks.includes(deck.id)) {
          card.decks.push(deck.id);
          await saveCard(card);
        }
      }
    }
    
    await removeDeletedItem(deleted.id);
    
    return {
      message: 'Deck restored',
      deck_id: deck.id,
      deck_name: deck.name
    };
  }
  
  if (deleted.item_type === 'workspace') {
    const workspace = deleted.item_data;
    workspace.is_deleted = false;
    workspace.deleted_at = null;
    workspace.updated_at = timestamp;
    await saveWorkspace(workspace);
    
    let cardsRestored = 0;
    let decksRestored = 0;
    
    for (const cascade of deleted.cascade_data || []) {
      for (const card of cascade.cards || []) {
        card.is_deleted = false;
        card.deleted_at = null;
        card.updated_at = timestamp;
        await saveCard(card);
        cardsRestored++;
      }
      for (const deck of cascade.decks || []) {
        deck.is_deleted = false;
        deck.deleted_at = null;
        deck.updated_at = timestamp;
        await saveDeck(deck);
        decksRestored++;
      }
    }
    
    await removeDeletedItem(deleted.id);
    
    return {
      message: 'Workspace restored',
      workspace_id: workspace.id,
      workspace_name: workspace.name,
      cards_restored: cardsRestored,
      decks_restored: decksRestored
    };
  }
  
  throw new Error('Unknown item type');
}

async function removeDeletedItem(id) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.DELETED_ITEMS, 'readwrite');
    const store = tx.objectStore(STORES.DELETED_ITEMS);
    const request = store.delete(id);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

// ============== SETTINGS ==============

export async function getSetting(key) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SETTINGS, 'readonly');
    const store = tx.objectStore(STORES.SETTINGS);
    const request = store.get(key);
    
    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
}

export async function saveSetting(key, value) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SETTINGS, 'readwrite');
    const store = tx.objectStore(STORES.SETTINGS);
    const request = store.put({ key, value });
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

export async function deleteSetting(key) {
  await initDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.SETTINGS, 'readwrite');
    const store = tx.objectStore(STORES.SETTINGS);
    const request = store.delete(key);
    
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
  });
}

// ============== FULL BACKUP/RESTORE ==============

export async function exportAllData() {
  const workspaces = await getWorkspaces();
  const allData = {
    version: 1,
    exported_at: now(),
    workspaces: []
  };
  
  for (const workspace of workspaces) {
    const cards = await getCards(workspace.id);
    const decks = await getDecks(workspace.id);
    
    allData.workspaces.push({
      ...workspace,
      cards,
      decks
    });
  }
  
  return allData;
}

export async function importAllData(data) {
  if (!data.workspaces) throw new Error('Invalid backup format');
  
  // Clear existing data
  await clearAllData();
  
  // Import workspaces, cards, decks
  for (const wsData of data.workspaces) {
    const { cards, decks, ...workspace } = wsData;
    await saveWorkspace(workspace);
    
    for (const card of cards || []) {
      await saveCard(card);
    }
    
    for (const deck of decks || []) {
      await saveDeck(deck);
    }
  }
  
  return {
    message: 'Backup restored',
    workspaces: data.workspaces.length,
    cards: data.workspaces.reduce((sum, ws) => sum + (ws.cards?.length || 0), 0),
    decks: data.workspaces.reduce((sum, ws) => sum + (ws.decks?.length || 0), 0)
  };
}

export async function clearAllData() {
  await initDB();
  
  const stores = [STORES.WORKSPACES, STORES.CARDS, STORES.DECKS, STORES.DELETED_ITEMS];
  
  for (const storeName of stores) {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.clear();
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
    });
  }
}

// ============== STATS ==============

export async function getStats(workspaceId) {
  const cards = await getCards(workspaceId);
  const decks = await getDecks(workspaceId);
  
  return {
    total_cards: cards.length,
    total_decks: decks.length
  };
}

